import { FieldsConfigResponse } from "@hooks/use-config-context/types";
import { DataTableCellValue, TableCellInputType } from "@components/data-table/types";
import { formatNullCellValue } from "@components/data-table/utils";
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Link, Stack, Typography } from "@mui/material";
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { underscoreToTitleCase } from "@utils/index";
import { WpFieldConfig } from "@hooks/use-config/types";
import { TableColumnFromApi } from "@hooks/use-config-context/types";
import { useEffect, useMemo, useState } from "react";
import { useFileUpload } from "@hooks/use-file-upload";
import { WpMediaPost } from "@components/file-display-list/types";

const normalizeFileIds = (value: DataTableCellValue): string[] => {
  if (value == null) return [];

  const cleanToken = (token: string): string =>
    token
      .replace(/[[\]"]+/g, "")
      .trim();

  const isLikelyFileId = (token: string): boolean => /^\d{5,}$/.test(token);

  const idsFromString = (input: string): string[] => {
    const trimmed = input.trim();
    if (trimmed === "") return [];

    return trimmed
      .split(",")
      .map(cleanToken)
      .filter((token) => token !== "" && isLikelyFileId(token));
  };

  const flattenToStrings = (arr: unknown[]): string[] =>
    arr.flat(Infinity)
      .map((v) =>
        typeof v === "number"
          ? String(v)
          : typeof v === "string"
            ? cleanToken(v)
            : "",
      )
      .filter((v) => v !== "" && isLikelyFileId(v));

  if (Array.isArray(value)) {
    return flattenToStrings(value);
  }

  if (typeof value === "number") {
    const numericId = String(value);
    return isLikelyFileId(numericId) ? [numericId] : [];
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "") return [];

    if (trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed);
        return Array.isArray(parsed) ? flattenToStrings(parsed) : [];
      } catch {
        return idsFromString(trimmed);
      }
    }

    return idsFromString(trimmed);
  }

  return [];
};

const extractFileNameFromUrl = (url: string): string => {
  try {
    const parsed = new URL(url);
    const lastSegment = parsed.pathname.split("/").filter(Boolean).pop() || url;
    return decodeURIComponent(lastSegment);
  } catch {
    return url;
  }
};

const TicketFileNamesCell = ({ value }: { value: DataTableCellValue }) => {
  const fileIds = useMemo(() => normalizeFileIds(value), [value]);
  const fileIdsKey = fileIds.join(",");
  const { wpFiles, fetchWpFiles, loading } = useFileUpload();
  const [fetchError, setFetchError] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState("");
  const [selectedImageTitle, setSelectedImageTitle] = useState("");

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (fileIds.length === 0) {
        if (!cancelled) setFetchError(false);
        return;
      }

      try {
        if (!cancelled) setFetchError(false);
        await fetchWpFiles(fileIds);
      } catch {
        if (!cancelled) setFetchError(true);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
    // fileIdsKey is a stable content-based proxy for fileIds, used to avoid refetching on new-but-equal array references
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileIdsKey, fetchWpFiles]);

  if (fileIds.length === 0) {
    return <Typography variant="caption">{formatNullCellValue(value)}</Typography>;
  }

  if (loading) {
    return <Typography variant="caption">Loading files...</Typography>;
  }

  if (fetchError) {
    return <Typography variant="caption">Unable to load file metadata</Typography>;
  }

  if (!wpFiles || wpFiles.length === 0) {
    return <Typography variant="caption">Files unavailable</Typography>;
  }

  const isImageFile = (file: WpMediaPost) => {
    if (file?.mime_type) return file.mime_type.startsWith("image/");
    return /\.(png|jpg|jpeg|gif|webp|bmp|svg)$/i.test(file.source_url || "");
  };

  const openLightbox = (file: WpMediaPost) => {
    setSelectedImageUrl(file.source_url);
    setSelectedImageTitle(file.title?.rendered || "Image preview");
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setSelectedImageUrl("");
    setSelectedImageTitle("");
  };

  return (
    <>
      <Stack spacing={0.25} sx={{ alignItems: "flex-start" }}>
        {wpFiles.map((file: WpMediaPost) => {
          const title = file?.title?.rendered?.trim();
          const fileName = title && title !== ""
            ? title
            : extractFileNameFromUrl(file.source_url);

          if (isImageFile(file)) {
            return (
              <Link
                key={file.id}
                component="button"
                type="button"
                underline="hover"
                onClick={() => openLightbox(file)}
                sx={{ fontSize: "0.75rem", lineHeight: 1.35, textAlign: "left" }}
              >
                {fileName}
              </Link>
            );
          }

          return (
            <Link
              key={file.id}
              href={file.source_url}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              sx={{ fontSize: "0.75rem", lineHeight: 1.35 }}
            >
              {fileName}
            </Link>
          );
        })}
      </Stack>

      <Dialog open={lightboxOpen} onClose={closeLightbox} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ pr: 6 }}>
          {selectedImageTitle}
          <IconButton
            aria-label="Close image preview"
            onClick={closeLightbox}
            sx={{ position: "absolute", right: 12, top: 12 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedImageUrl ? (
            <Box
              component="img"
              src={selectedImageUrl}
              alt={selectedImageTitle || "Image preview"}
              sx={{ width: "100%", maxHeight: "70vh", objectFit: "contain" }}
            />
          ) : (
            <Typography variant="body2">Unable to load image preview.</Typography>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export const ticketTableCellFormatters = (
  columns: TableColumnFromApi<any>[],
  config: FieldsConfigResponse
) => {
  const checkIcon = (
    <CheckIcon sx={{ color: (theme) => theme.palette.success.main }} />
  );
  const closeIcon = (
    <CloseIcon sx={{ color: (theme) => theme.palette.error.main }} />
  );

  const booleanFormatter = (value: DataTableCellValue) => {
    if (value === true || value === "Yes" || value === "1" || value === 1) {
      return checkIcon
    } else {
      return closeIcon;
    }
  };

  const formFormatter = (value: DataTableCellValue) => {
    if (typeof value === "string" && value !== "") {
      try {
        return Object.entries(JSON.parse(value)).map(([key, val]) => {
          const isFilesGroupField = key === "files_group" || /file/i.test(key);

          return (
            <Box key={key}>
              <Typography variant="caption" fontWeight={600}>
                {underscoreToTitleCase(key)}:
              </Typography>{" "}
              {isFilesGroupField
                ? <TicketFileNamesCell value={val as DataTableCellValue} />
                : (
                  <Typography variant="caption">
                    {formatNullCellValue(val)}
                  </Typography>
                )}
            </Box>
          );
        });
      } catch {
        return formatNullCellValue(value);
      }
    }
    return formatNullCellValue(value);
  };

  const selectFormatter =
    (assetConfig: WpFieldConfig) => (value: DataTableCellValue) => {
      if (typeof value === "string" && value !== "" && value.includes("{")) {
        const parsedValue = JSON.parse(value);
        return parsedValue?.name ?? "";
      }
      if (assetConfig?.options) {
        return (
          (assetConfig.options as any[]).find(
            (option) =>
              option.value === value || option.value?.toString() === value
          )?.label || formatNullCellValue(value)
        );
      }
      return formatNullCellValue(value);
    };

  const formatters = columns.reduce((acc, column) => {
    const assetConfig = config[column.id];
    const isLikelyFileColumn =
      column.id === "files_group" ||
      /file/i.test(column.id) ||
      /file/i.test(column.label || "");

    if (isLikelyFileColumn) {
      acc[column.id] = (value: DataTableCellValue) => <TicketFileNamesCell value={value} />;
      return acc;
    }

    if (column.inputType === TableCellInputType.Boolean || column.displayType === TableCellInputType.Boolean) {
      acc[column.id] = booleanFormatter;
    }
    if (column.inputType === TableCellInputType.Form) {
      acc[column.id] = formFormatter;
    }
    if (column.inputType === TableCellInputType.Select) {
      acc[column.id] = selectFormatter(assetConfig);
    }
    if (column.inputType === TableCellInputType.UserInput) {
      acc[column.id] = selectFormatter(assetConfig);
    }
    if (column.inputType === TableCellInputType.FileUpload) {
      acc[column.id] = (value: DataTableCellValue) => <TicketFileNamesCell value={value} />;
    }
    return acc;
  }, {} as Record<string, (value: DataTableCellValue) => React.ReactNode>);

  return formatters;
};